// Meetings (brief update 2, section 18; docs/PLAN.md step 5): planned and held meetings, the topics
// waiting for the next one, and a new meeting. Nothing is sent anywhere: the days, time and channel
// are not configured (the notice says so).
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useContextWorkspace } from '../components/Layout';
import { Audience, Chip, Demo, Empty, ErrorBox, Field, Loading, PageHead } from '../components/ui';
import { api, errorText } from '../lib/api';
import { day } from '../lib/format';
import type { MeetingDetail, MeetingList } from '../lib/types';
import { useApi } from '../lib/useApi';

export function Meetings() {
  const ws = useContextWorkspace()!;
  const list = useApi<MeetingList>(`/api/w/${ws.slug}/meetings`);
  const d = list.data;
  const planned = d?.meetings.filter((m) => m.status === 'planned') ?? [];
  const held = d?.meetings.filter((m) => m.status === 'held') ?? [];

  return (
    <>
      <PageHead title="Meetings" sub={`Agendas and notes of ${ws.name}.`} />
      {d && !d.delivery.configured && <p className="msg">{d.delivery.reason}</p>}
      <section className="panel">
        <h2>New meeting</h2>
        <NewMeeting />
      </section>
      {list.error && <ErrorBox error={list.error} retry={list.reload} />}
      {!d && !list.error && <Loading what="Loading meetings" />}
      {d && (
        <>
          <section className="panel" aria-label="Topics waiting for the next meeting">
            <h2>Waiting for the next meeting</h2>
            <p className="muted small">Topics not discussed last time and questions left open. The next new meeting takes them.</p>
            {d.waiting.length ? (
              <ul className="recs">
                {d.waiting.map((t) => (
                  <li key={t.id} className="rec">
                    <div className="rec-main">
                      <span className="rec-title">{t.subject}</span>
                      <div className="rec-meta">
                        <Chip value={t.goal} />
                        {t.raised_by && <span className="muted small">raised by {t.raised_by}</span>}
                        {t.carried && <span className="badge">carried</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>Nothing is waiting.</Empty>
            )}
          </section>
          {[
            ['Planned', planned, 'No meeting planned.'],
            ['Held', held, 'No meeting held yet.'],
          ].map(([title, rows, empty]) => (
            <section className="panel" key={title as string}>
              <h2>{title as string}</h2>
              {(rows as MeetingList['meetings']).length ? (
                <ul className="recs">
                  {(rows as MeetingList['meetings']).map((m) => (
                    <li key={m.id} className="rec">
                      <div className="rec-main">
                        <span className="rec-title">
                          <Link to={`/w/${ws.slug}/meetings/${m.id}`}>{m.title}</Link>
                        </span>
                        <div className="rec-meta">
                          <span className="muted small">{m.held_on ? day(m.held_on) : 'no date yet'}</span>
                          <Audience item={m} />
                          <Demo show={m.demo} />
                          {m.has_notes && <span className="badge">notes</span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty>{empty as string}</Empty>
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}

function NewMeeting() {
  const ws = useContextWorkspace()!;
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [isPrivate, setPrivate] = useState(false);
  const [error, setError] = useState('');

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const m = await api<MeetingDetail>(`/api/w/${ws.slug}/meetings`, { body: { title, held_on: date || undefined, private: isPrivate } });
      navigate(`/w/${ws.slug}/meetings/${m.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <form onSubmit={create} aria-label="New meeting">
      <div className="row gap wrap">
        <Field label="Title">
          <input required value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Monday meeting" />
        </Field>
        <Field label="Date (optional)">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      <label className="check">
        <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} /> Only me (private preparation)
      </label>
      {error && <p className="msg err">{error}</p>}
      <button type="submit" className="btn primary" disabled={!title.trim()}>
        Create the meeting
      </button>
    </form>
  );
}
